import React, { useCallback, useEffect, useRef, useState } from 'react';
import { titleOf, withGroupDisplayTitles } from '../../utils/blockTitleYear';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Hamburger from '../Hamburger';
import ErrorPopUp from '../pages/BulkVestmaps/ErrorPopUp';
import styles from './globalStyles.module.css';
import { MessageSquare, Share } from 'lucide-react';
import { useAuth } from '../../AuthProvider';
import { useNavigate } from 'react-router-dom';
import { AGENT_ENABLED } from '../../featureFlags';
import {
  buildShadedFeatureLayer,
  requestReportShading,
  SHADING_UNAVAILABLE_MESSAGE,
  retitleForVintage,
} from '../../utils/reportShading';
import {
  applyDiscernShading,
  addUnavailableNotice,
  removeRetiredLayers,
} from '../../utils/discernWebmapShading';
import HeaderImage from '../VestmapViewer/pdf_img/vestmap_logo.png';
import { ReactComponent as DemoIconDark } from '@/../../public/demoIconDark.svg';
import { ReactComponent as DemoIconLight } from '@/../../public/demoIconLight.svg';
import { ReactComponent as IncomeIconDark } from '@/../../public/incomeIconDark.svg';
import { ReactComponent as IncomeIconLight } from '@/../../public/incomeIconLight.svg';
import { ReactComponent as HPIIconDark } from '@/../../public/hpiIconDark.svg';
import { ReactComponent as HPIIconLight } from '@/../../public/hpiIconLight.svg';
import { ReactComponent as SchoolsIconDark } from '@/../../public/schoolIconDark.svg';
import { ReactComponent as SchoolsIconLight } from '@/../../public/schoolIconLight.svg';
import { ReactComponent as CrimeIconDark } from '@/../../public/crimeIconDark.svg';
import { ReactComponent as CrimeIconLight } from '@/../../public/crimeIconLight.svg';
import { ReactComponent as ExpansionIconDark } from '@/../../public/expansionIconDark.svg';
import { ReactComponent as ExpansionIconLight } from '@/../../public/expansionIconLight.svg';
import { ReactComponent as RentIconDark } from '@/../../public/rentIconDark.svg';
import { ReactComponent as RentIconLight } from '@/../../public/rentIconLight.svg';
import { ReactComponent as NeighborhoodIconDark } from '@/../../public/neighborhoodIconDark.svg';
import { ReactComponent as NeighborhoodIconLight } from '@/../../public/neighborhoodIconLight.svg';
import { ReactComponent as MSAIconDark } from '@/../../public/msaIconDark.svg';
import { ReactComponent as MSAIconLight } from '@/../../public/msaIconLight.svg';
import { ReactComponent as AiAnalysisDark } from '@/../../public/flaskDark.svg';
import { ReactComponent as AiAnalysisLight } from '@/../../public/flaskLight.svg';

// Import components from CustomVestmapViewer
import GenericBlock from '../CustomVestmapViewer/components/GenericBlock';
import PriceAppreciation from '../CustomVestmapViewer/components/PriceAppreciation';
import ChartBlock from '../CustomVestmapViewer/components/ChartBlock';
import ExpansionBlock from '../CustomVestmapViewer/components/ExpansionBlock';
import RentBlock from '../CustomVestmapViewer/components/RentBlock';
import NationalAverageCrimeIndex from '../CustomVestmapViewer/components/NationalAverageCrimeIndex';
import NearestThreeSchoolsBlock from '../CustomVestmapViewer/components/Schools';
import BlockNameHeading from '../CustomVestmapViewer/components/BlockNameHeading';
import colors from '../VestmapViewer/Demographics/colors.json';
import data from '../VestmapViewer/Overlay/data.json';
import Groups from '../VestmapViewer/Demographics/groups.json';
import Overlay from '../VestmapViewer/Overlay';
import QuestionMarkSVG from '../CustomVestmapViewer/components/QuestionMarkSVG';

// Import map-related dependencies
import { loadModules } from 'esri-loader';
import marker from '../CustomVestmapViewer/assets/marker center.png';
import { requestThrottler } from '../../services/requestThrottler';

// Global Map Mount Manager to cap max concurrently mounted maps on mobile
const MapMountManager = (() => {
  const MAX_ACTIVE = 2;
  const activeList = [];
  const registry = new Map();

  function acquireSlot(id, unmountFn) {
    const existingIndex = activeList.findIndex(entry => entry.id === id);
    if (existingIndex !== -1) {
      registry.set(id, unmountFn);
      activeList[existingIndex].unmount = unmountFn;
      return true;
    }

    if (activeList.length < MAX_ACTIVE) {
      registry.set(id, unmountFn);
      activeList.push({ id, unmount: unmountFn });
      return true;
    }

    const evicted = activeList.shift();
    if (evicted && typeof evicted.unmount === 'function') {
      try {
        evicted.unmount();
      } catch (_) {}
    }
    registry.delete(evicted?.id);

    registry.set(id, unmountFn);
    activeList.push({ id, unmount: unmountFn });
    return true;
  }

  function releaseSlot(id) {
    registry.delete(id);
    const index = activeList.findIndex(entry => entry.id === id);
    if (index !== -1) {
      activeList.splice(index, 1);
    }
  }

  return { acquireSlot, releaseSlot };
})();

// Mobile-only lazy map gate with auto-mount and global cap (aligns with other viewers)
const MobileLiveMapGate = ({
  renderMap,
  placeholderTitle = 'Interactive Map',
}) => {
  const containerRef = useRef(null);
  const [isShown, setIsShown] = useState(false);
  const [isInView, setIsInView] = useState(true);
  const instanceIdRef = useRef(
    `map_gate_${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    // initial attempt after layout settles
    let retryTimer = null;
    const tryAcquire = () => {
      if (!containerRef.current || isShown) return;
      const rect = containerRef.current.getBoundingClientRect();
      const visible = rect.top < window.innerHeight && rect.bottom > 0;
      if (visible) {
        const ok = MapMountManager.acquireSlot(instanceIdRef.current, () => {
          setIsShown(false);
        });
        if (ok) setIsShown(true);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tryAcquire();
      });
    });
    retryTimer = setTimeout(tryAcquire, 250);

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      MapMountManager.releaseSlot(instanceIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        const visible = entry.isIntersecting && entry.intersectionRatio > 0;
        setIsInView(visible);
      },
      { root: null, rootMargin: '0px', threshold: [0, 0.01, 0.1] },
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto mount when entering view (respecting global limit). Eviction only via manager.
  useEffect(() => {
    if (isInView && !isShown) {
      const ok = MapMountManager.acquireSlot(instanceIdRef.current, () => {
        setIsShown(false);
      });
      if (ok) setIsShown(true);
    }
  }, [isInView, isShown]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '10px',
      }}
    >
      {!isShown ? (
        <Box
          sx={{
            p: 2,
            border: '1px solid rgba(64,196,170,0.3)',
            borderRadius: '8px',
            background: 'rgba(64,196,170,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant='body2'
              sx={{ color: '#39585C', fontWeight: 600 }}
            >
              {placeholderTitle}
            </Typography>
          </Box>
        </Box>
      ) : (
        renderMap()
      )}
    </Box>
  );
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

const CombinedReportViewer = ({
  open,
  setOpen,
  groupData,
  viewerState = () => {},
  publicView = false,
}) => {
  const { getAccessToken } = useAuth();
  const [group, setGroupRaw] = useState(() => withGroupDisplayTitles(groupData || {}));
  // Adds vintage-aware displayTitle fields to every report's sections/blocks;
  // stored titles stay as-is because some components key lookups on them.
  const setGroup = useCallback(next => setGroupRaw(withGroupDisplayTitles(next)), []);
  // Removed PDF/Pro generation controls for Combined viewer
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingPro, setGeneratingPro] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [proReport, setProReport] = useState(null);
  const [limitError, setLimitError] = useState(false);
  const [errorString, setErrorString] = useState('');
  const [accessToken, setAccessToken] = useState(null);
  const [loadingToken, setLoadingToken] = useState(false);

  useEffect(() => {
    setGroup(groupData);
  }, [groupData]);

  // Fetch ArcGIS token for maps
  useEffect(() => {
    async function getArcgisToken() {
      setLoadingToken(true);
      try {
        const url = process.env.REACT_APP_NODE_URL + '/admin/token';
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });

        if (!response.ok) throw new Error('Response error');

        const data = await response.json();
        if (data?.token) {
          setAccessToken(data.token.access_token);
          setLoadingToken(false);
          return;
        }

        throw new Error('Token not found');
      } catch (error) {
        console.error('Error getting token:', error);
        setLoadingToken(false);
      }
    }

    getArcgisToken();
  }, [getAccessToken]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [currentSection]);

  const handleClose = () => {
    // clear all states before closing (preserve group so it renders on reopen)
    setGeneratingPDF(false);
    setGeneratingPro(false);
    setPdf(null);
    setProReport(null);

    setOpen(false);
    viewerState(false);
  };

  function closeLimitError() {
    setLimitError(false);
  }

  console.debug({
    debugLog: { groupData },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll='body'
      fullWidth={true}
      aria-labelledby='scroll-dialog-title'
      aria-describedby='scroll-dialog-description'
      className={styles.dialog}
      PaperProps={{
        sx: {
          boxSizing: 'border-box',
          borderRadius: {
            sm: '20px',
            xs: '0px',
          },
          overflow: {
            xs: sidebarOpen && 'hidden',
          },
        },
      }}
      fullScreen={true}
      id='combinedReportDialogBox'
      onClick={() => {
        if (sidebarOpen) {
          setSidebarOpen(false);
        }
      }}
      disableEscapeKeyDown={publicView ? true : false}
    >
      {limitError && (
        <ErrorPopUp closeLimitError={closeLimitError} error={errorString} />
      )}
      <DialogContent className={styles.dialogContent}>
        <Box
          sx={{
            display: {
              xs: 'flex',
              sm: 'grid',
            },
            gridTemplateColumns: {
              xs: '100%',
              sm: '22% 78%',
              md: '18% 82%',
            },
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: {
                xs: 'none',
                sm: 'block',
              },
              position: 'sticky',
              top: 0,
              height: '100vh',
              overflowY: 'auto',
            }}
          >
            <Sidebar
              group={group}
              setSidebarOpen={setSidebarOpen}
              activeSection={activeSection}
              publicView={publicView}
            />
          </Box>

          {/* For smaller screen */}
          <Box
            sx={{
              display: {
                xs: 'block',
                sm: 'none',
              },
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 100,
              width: '60%',
              height: '100%',
              overflowY: 'auto',
              transition: 'transform 0.3s',
              transform: {
                xs: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                sm: 'none',
              },
            }}
          >
            <Sidebar
              group={group}
              setSidebarOpen={setSidebarOpen}
              activeSection={activeSection}
              publicView={publicView}
            />
          </Box>

          <Box
            sx={{
              overflowY: {
                sm: 'auto',
                xs: 'none',
              },
              height: {
                sm: '100vh',
              },
              scrollbarWidth: 'none',
              maxWidth: '100vw',
            }}
          >
            <DialogTitle
              id='scroll-dialog-title'
              className={styles.dialogHeader}
              sx={{
                backgroundColor: '#F9F9F9',
                px: { xs: 1.5, sm: 3 },
                py: { xs: 1, sm: 2 },
                '& h2': {
                  fontFamily: 'Lato',
                  fontWeight: 800,
                  fontSize: { xs: '1.1rem', sm: '1.4rem' },
                  lineHeight: { xs: 1.25, sm: 1.2 },
                  textAlign: 'center',
                  margin: 0,
                  wordBreak: 'break-word',
                },
              }}
            >
              {!publicView && (
                <IconButton
                  aria-label='close'
                  onClick={setSidebarOpen.bind(this, !sidebarOpen)}
                  sx={{
                    position: 'absolute',
                    display: { xs: 'block', sm: 'none' },
                    left: 11,
                    top: 11,
                    height: '40px',
                    width: '40px',
                    zIndex: 1001,
                    color: theme => theme.palette.grey[500],
                  }}
                >
                  <Hamburger isOpen={sidebarOpen} headerFlag={false} />
                </IconButton>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: '16px', sm: '40px' },
                  padding: { xs: '12px', sm: '24px' },
                }}
              >
                <Box
                  sx={{
                    width: {
                      sm: '100%',
                      xs: '160px',
                    },
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <img src={HeaderImage} alt='' />
                </Box>
                <div className={styles.dialogHeaderChild}>
                  <h2>
                    Combined Report:{' '}
                    {group?.reports?.[0]?.address_info?.address ||
                      group?.reports?.[0]?.addressInfo?.address ||
                      'Chat Session Reports'}
                  </h2>
                  <Typography
                    sx={{
                      fontFamily: 'Lato',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      color: '#6b7280',
                      textAlign: 'center',
                      marginTop: { xs: '0.25rem', sm: '0.5rem' },
                    }}
                  >
                    {group?.reports?.length || 0} Reports Combined
                  </Typography>
                </div>
              </Box>

              {!publicView && (
                <IconButton
                  aria-label='close'
                  onClick={handleClose}
                  sx={{
                    position: 'fixed',
                    right: 11,
                    top: 11,
                    zIndex: 4,
                    height: '40px',
                    width: '40px',
                    color: theme => theme.palette.grey[500],
                  }}
                >
                  <Hamburger isOpen={true} headerFlag={false} />
                </IconButton>
              )}
            </DialogTitle>

            <CombinedReportContent
              group={group}
              sectionRefs={sectionRefs}
              setActiveSection={setActiveSection}
              accessToken={accessToken}
              loadingToken={loadingToken}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Box
          sx={{
            width: {
              sm: '79.7%',
              xs: '100%',
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: {
              sm: '18px',
              xs: '14px',
            },
            gap: {
              sm: '16px',
              xs: '12px',
            },
          }}
        >
          <Box
            sx={{
              color: '#84939C',
              fontFamily: 'Lato',
              fontWeight: '700',
            }}
          >
            Combined Report Generated by
          </Box>
          <Box
            sx={{
              width: {
                sm: '200px',
                xs: '150px',
              },
              height: {
                sm: '30px',
                xs: '27px',
              },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img src={HeaderImage} alt='Vestmap Logo' />
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

function CombinedReportContent({
  group,
  setActiveSection,
  accessToken,
  loadingToken,
}) {
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (group?.reports) {
      // Sort reports by updated date (oldest first)
      const sortedReports = [...group.reports].sort((a, b) => {
        const dateA = new Date(
          a.updatedAt || a.updated_at || a.createdAt || a.created_at || 0,
        );
        const dateB = new Date(
          b.updatedAt || b.updated_at || b.createdAt || b.created_at || 0,
        );
        return dateA - dateB;
      });
      setReports(sortedReports);
    }
  }, [group]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: [0.5, 0.3, 0.2, 1],
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionKey = entry.target.getAttribute('data-section-key');
          if (sectionKey) {
            setActiveSection(sectionKey);
          }
        }
      });
    }, observerOptions);

    // Observe all sections across all reports
    const allSections = document.querySelectorAll('[data-section-key]');
    allSections.forEach(section => {
      observer.observe(section);
    });

    return () => {
      allSections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, [setActiveSection, reports]);

  if (!reports || reports.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <Typography>No reports available to display</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: '0' }, width: '100%' }}>
      {reports.map((report, reportIndex) => (
        <ReportDisplay
          key={report._id}
          report={report}
          reportIndex={reportIndex}
          totalReports={reports.length}
          accessToken={accessToken}
          loadingToken={loadingToken}
        />
      ))}
    </Box>
  );
}

// ReportDisplay component that shows a complete report following CustomVestmapViewer UI
function ReportDisplay({
  report,
  reportIndex,
  totalReports,
  accessToken,
  loadingToken,
}) {
  const [currentSection, setCurrentSection] = useState('');
  const orderedSections = React.useMemo(() => {
    const sections = Array.isArray(report?.sections)
      ? [...report.sections]
      : [];
    const templateName = report?.templateName || report?.template?.templateName;
    if (templateName === 'Default (DISCERN)') {
      const order = [
        'demographics',
        'income',
        'hpi',
        'schools',
        'crime',
        'expansion',
        'rent',
        'neighborhood',
        'msa',
      ];
      sections.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    }
    return sections;
  }, [report]);

  const getIconForSection = sectionKey => {
    const icons = {
      demographics: (
        <DemoIconDark style={{ height: '2.25rem', width: '2.25rem' }} />
      ),
      income: (
        <IncomeIconDark style={{ height: '2.25rem', width: '2.25rem' }} />
      ),
      hpi: <HPIIconDark style={{ height: '2.25rem', width: '2.25rem' }} />,
      schools: (
        <SchoolsIconDark style={{ height: '2.25rem', width: '2.25rem' }} />
      ),
      crime: <CrimeIconDark style={{ height: '2.25rem', width: '2.25rem' }} />,
      expansion: (
        <ExpansionIconDark style={{ height: '2.25rem', width: '2.25rem' }} />
      ),
      rent: <RentIconDark style={{ height: '2.25rem', width: '2.25rem' }} />,
      neighborhood: (
        <NeighborhoodIconDark style={{ height: '2.25rem', width: '2.25rem' }} />
      ),
      msa: <MSAIconDark style={{ height: '2.25rem', width: '2.25rem' }} />,
      ai_analysis: (
        <AiAnalysisDark style={{ height: '2.25rem', width: '2.25rem' }} />
      ),
    };

    return icons[sectionKey];
  };

  const renderSection = (section, sectionIndex) => {
    if (!section) return null;

    return (
      <Accordion
        key={sectionIndex}
        defaultExpanded={true}
        slotProps={{ unmountOnExit: true }}
        sx={{
          borderRadius: '16px !important',
          boxShadow: '5px 20px 30px 0px #E5E5E5B2',
          scrollbarWidth: 'none',
          width: '100%',
        }}
        id={`${report._id}-${section.key}`}
        data-section-key={section.key}
      >
        <AccordionSummary
          id={`panel${sectionIndex}bh-header`}
          sx={{
            pointerEvents: 'none',
            display: 'flex',
            gap: '1rem',
            width: '100%',
          }}
        >
          {getIconForSection(section.key) && (
            <div style={{ marginRight: '1rem' }}>
              {getIconForSection(section.key)}
            </div>
          )}
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '24px',
              letterSpacing: '0.15px',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {section.title.includes('Income')
              ? 'Income'
              : section.title.includes('MSA')
              ? 'Metropolitan Statistical Area'
              : titleOf(section)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionContent
            section={section}
            report={report}
            token={accessToken}
            loadingToken={loadingToken}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  if (!report) return null;

  return (
    <Box
      sx={{
        marginBottom: '3rem',
        border: '3px solid #e5e7eb',
        borderRadius: '1.5rem',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      {/* Report Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4FA490 0%, #3a7a6f 100%)',
          padding: '1.25rem',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'radial-gradient(circle at 20px 20px, rgba(255,255,255,0.1) 2px, transparent 2px)',
            backgroundSize: '40px 40px',
            opacity: 0.3,
          },
        }}
      >
        {/* Top Row with House Icon and Report Number */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '1rem',
          }}
        >
          {/* Left Side - House Icon and Report Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}
          >
            {/* House Icon Container */}
            <Box
              sx={{
                position: 'relative',
                width: '45px',
                height: '45px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {/* House Icon */}
              <Box
                sx={{
                  width: '24px',
                  height: '24px',
                  background: 'white',
                  clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                }}
              />
              {/* Report Number Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#6BCF7F',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  fontSize: '0.65rem',
                  fontWeight: '700',
                  color: 'white',
                }}
              >
                {reportIndex + 1}
              </Box>
            </Box>

            {/* Report Title */}
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: { xs: '1rem', sm: '1.4rem' },
                fontWeight: { xs: 700, sm: 800 },
                color: 'white',
                lineHeight: { xs: 1.15, sm: 1.2 },
              }}
            >
              Report {reportIndex + 1} of {totalReports}
            </Typography>
          </Box>

          {/* Right Side - Template Name */}
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              padding: '0.5rem 1rem',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              display: { xs: 'none', sm: 'block' }, // Hide on mobile, show on sm+
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '0.65rem',
                fontWeight: '600',
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.2rem',
              }}
            >
              Template
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '0.9rem',
                fontWeight: '700',
                color: 'white',
              }}
            >
              {report.templateName ||
                report.template?.templateName ||
                'Default'}
            </Typography>
          </Box>
        </Box>

        {/* Template Tags */}
        <Box
          sx={{
            display: 'flex',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}
        >
          {/* Primary Template Tag */}
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              padding: '0.4rem 0.8rem',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: 'white',
              }}
            >
              {report.templateName ||
                report.template?.templateName ||
                'Default Template'}
            </Typography>
          </Box>

          {/* Secondary Template Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.4rem 0.8rem',
              color: 'white',
              opacity: 0.8,
            }}
          >
            <Box
              sx={{
                width: '5px',
                height: '5px',
                backgroundColor: 'white',
                borderRadius: '50%',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '0.8rem',
                fontWeight: '500',
                color: 'white',
              }}
            >
              {report.templateType || 'Standard Report'}
            </Typography>
          </Box>
        </Box>

        {/* Address Section */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
            }}
          >
            {/* Location Pin Icon */}
            <Box
              sx={{
                width: '16px',
                height: '16px',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '0',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderBottom: '10px solid white',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '0',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '6px',
                  height: '6px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                },
              }}
            />

            {/* Address Text */}
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '0.85rem',
                fontWeight: '500',
                color: 'white',
                lineHeight: '1.4',
              }}
            >
              {report.address_info?.address ||
                report.addressInfo?.address ||
                'Unknown Address'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Report Sections */}
      <Box sx={{ padding: { xs: '0', sm: '24px' } }}>
        {orderedSections?.map(
          (section, index) =>
            !section.key.includes('msa') && renderSection(section, index),
        )}
      </Box>
    </Box>
  );
}

// SectionContent component to render individual section content
function SectionContent({ section, report, token, loadingToken }) {
  const isMobile = useIsMobile();
  if (!section?.dynamicBlocks) return null;

  const renderIncomeBlocks = income => {
    if (!income) return null;
    return (
      <Box
        sx={{
          flexBasis: '100%',
          width: '100%',
          gap: '1.5rem',
          paddingY: { xs: '0', sm: '1rem' },
          paddingX: { xs: '0', sm: '1.75rem' },
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BlockNameHeading title='Median Household Income' />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <GenericBlock
            sectionBlock={{
              title: 'Block',
              value: { block: income?.block },
              outfields: ['block'],
              prefix: '$',
            }}
            tooltipTitle={'Block'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'Tract',
              value: { tract: income?.tract },
              outfields: ['tract'],
              prefix: '$',
            }}
            tooltipTitle={'Tract'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'Zip',
              value: { zip: income?.zip },
              outfields: ['zip'],
              prefix: '$',
            }}
            tooltipTitle={'Zip'}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <GenericBlock
            sectionBlock={{
              title: 'County',
              value: { county: income?.county },
              outfields: ['county'],
              prefix: '$',
            }}
            tooltipTitle={'County'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'State',
              value: { state: income?.state },
              outfields: ['state'],
              prefix: '$',
            }}
            tooltipTitle={'State'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'National',
              value: { national: income?.national },
              outfields: ['national'],
              prefix: '$',
            }}
            tooltipTitle={'National'}
          />
        </Box>
      </Box>
    );
  };

  const renderHomeValueBlocks = vals => {
    if (!vals) return null;
    return (
      <Box
        sx={{
          flexBasis: '100%',
          width: '100%',
          gap: '1.5rem',
          paddingY: { xs: '0', sm: '1rem' },
          paddingX: { xs: '0', sm: '1.75rem' },
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BlockNameHeading title='Median Home Value' />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <GenericBlock
            sectionBlock={{
              title: 'Block',
              value: { block: vals?.block },
              outfields: ['block'],
              prefix: '$',
            }}
            tooltipTitle={'MHVB'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'Tract',
              value: { tract: vals?.tract },
              outfields: ['tract'],
              prefix: '$',
            }}
            tooltipTitle={'MHVT'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'Zip',
              value: { zip: vals?.zip },
              outfields: ['zip'],
              prefix: '$',
            }}
            tooltipTitle={'MHVZ'}
          />
        </Box>
      </Box>
    );
  };

  const renderAFMIGBlocks = vals => {
    if (!vals) return null;
    return (
      <Box
        sx={{
          flexBasis: '100%',
          width: '100%',
          paddingY: '1rem',
          gap: '1.5rem',
          paddingX: { xs: '0', sm: '1.75rem' },
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BlockNameHeading title='Annual Forecasted Median Income Growth' />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <GenericBlock
            sectionBlock={{
              title: 'Tract',
              value: { tract: vals?.tract },
              outfields: ['tract'],
              suffix: '%',
            }}
            tooltipTitle={'AFMI'}
          />
          <GenericBlock
            sectionBlock={{
              title: 'Zip',
              value: { zip: vals?.zip },
              outfields: ['zip'],
              suffix: '%',
            }}
            tooltipTitle={'AFMI2'}
          />
        </Box>
      </Box>
    );
  };

  const renderBlock = (block, blockIndex) => {
    switch (block?.outfields?.[0]) {
      case 'group':
        return (
          <DemographicGroupBlock key={blockIndex} group={block?.value?.group} />
        );
      case 'nearest_groups':
        return (
          <NearestGroupsBlock
            key={blockIndex}
            groups={block?.value?.nearest_groups}
          />
        );
      case 'who_we_are':
        return (
          <WhoWeAreBlock
            key={blockIndex}
            blockValue={block?.value?.who_we_are}
          />
        );
      case 'nearest_3_schools':
        return (
          <NearestThreeSchoolsBlock
            key={blockIndex}
            schools={block?.value?.nearest_3_schools}
          />
        );
      case 'price_appreciation_since_2011':
        return (
          <PriceAppreciation
            key={blockIndex}
            blockValue={block?.value?.price_appreciation_since_2011}
            title={'Price Appreciation Since 2011'}
            runningValues={
              block?.value?.price_appreciation_since_2011?.runningValues
            }
          />
        );
      case 'background_rate_of_appreciation':
        return (
          <PriceAppreciation
            key={blockIndex}
            blockValue={block?.value?.background_rate_of_appreciation}
            title={'Background Rate of Appreciation (1990 - present)'}
            runningValues={
              block?.value?.background_rate_of_appreciation?.runningValues
            }
          />
        );
      case 'hpi_tract_zip_index': {
        const convertDataFormat = (zips, tracts, counties) => {
          if (!zips && !tracts && !counties) return null;
          const output = [];
          const years = Object.keys(zips || tracts || counties);
          years.forEach(key => {
            const year = key.split('_')[1];
            const zip = zips && zips[key];
            const tract = tracts && tracts[key];
            const county = counties && counties[key];
            output.push({
              name: year,
              zip: zip || null,
              tract: tract || null,
              county: county || null,
            });
          });
          return output;
        };
        const data = convertDataFormat(
          block?.value?.hpi_tract_zip_index?.zip,
          block?.value?.hpi_tract_zip_index?.tract,
          block?.value?.hpi_tract_zip_index?.county,
        );
        return <ChartBlock key={blockIndex} data={data} />;
      }
      case 'block':
      case 'tract':
      case 'zip':
      case 'county':
      case 'state':
      case 'national':
        return (
          <ExpansionBlock
            key={blockIndex}
            sectionBlock={block}
            tooltipTitle={`${block?.title || 'Expansion'}`}
          />
        );
      case 'rent_block':
        return <RentBlock key={blockIndex} rentBlock={block} />;
      case 'overall_crime_index_score':
      case 'property_crime_index_score':
        return (
          <NationalAverageCrimeIndex key={blockIndex} sectionBlock={block} />
        );
      case 'median_household_income':
        return renderIncomeBlocks(block?.value?.median_household_income);
      case 'median_home_value':
        return renderHomeValueBlocks(block?.value?.median_home_value);
      case 'annual_forecasted_median_income_growth':
        return renderAFMIGBlocks(
          block?.value?.annual_forecasted_median_income_growth,
        );
      default:
        if (block?.title === 'Grade') {
          return (
            <GenericBlock
              key={blockIndex}
              sectionBlock={block}
              tooltipTitle={'Grade'}
            />
          );
        }
        return <GenericBlock key={blockIndex} sectionBlock={block} />;
    }
  };

  return (
    <Box>
      {/* Map Display */}
      {section?.webmapID &&
        section.webmapID !== '' &&
        (() => {
          if (loadingToken) {
            return (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '10px',
                }}
              >
                <Skeleton variant='rectangular' width='100%' height={480} />
              </Box>
            );
          }
          if (!token) return null;

          if (!isMobile) {
            return (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '10px',
                }}
              >
                <LiveMap
                  webmapID={section.webmapID}
                  token={token}
                  selectedBlock={section.mapBlock}
                  defaultSettings={!section.mapBlock}
                  addressInfo={report?.addressInfo}
                  staticMapImage={report?.assets?.maps?.[`${section.key}_map`]}
                />
              </Box>
            );
          }

          return (
            <MobileLiveMapGate
              placeholderTitle={section.title || 'Interactive Map'}
              renderMap={() => (
                <LiveMap
                  webmapID={section.webmapID}
                  token={token}
                  selectedBlock={section.mapBlock}
                  defaultSettings={!section.mapBlock}
                  addressInfo={report?.addressInfo}
                  staticMapImage={report?.assets?.maps?.[`${section.key}_map`]}
                />
              )}
            />
          );
        })()}

      {/* Data Blocks */}
      {section?.key === 'crime' ? (
        <CrimeSection section={section} />
      ) : (
        <>
          {section?.key === 'msa' && (
            <BlockNameHeading title={'Metropolitan Statistical Area'} />
          )}
          {section?.key === 'expansion' &&
            (report?.templateName === 'Default (DISCERN)' ||
              report?.template?.templateName === 'Default (DISCERN)') && (
              <Box sx={{ paddingY: '0.75rem' }}>
                <BlockNameHeading
                  title={'Annual Forecasted Population Growth'}
                />
              </Box>
            )}

          {section.dynamicBlocks
            .reduce((rows, block, idx) => {
              if (idx % 3 === 0) rows.push([]);
              rows[rows.length - 1].push(block);
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginBottom: '10px',
                  width: '100%',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                {row.map((block, blockIndex) => renderBlock(block, blockIndex))}
              </Box>
            ))}
        </>
      )}
    </Box>
  );
}

function CrimeSection({ section }) {
  if (!section) return null;

  const renderBlock = (block, blockIndex) => {
    switch (block.outfields[0]) {
      case 'overall_crime_index_score': {
        return <NationalAverageCrimeIndex sectionBlock={block} />;
      }
      case 'property_crime_index_score': {
        return <NationalAverageCrimeIndex sectionBlock={block} />;
      }

      default: {
        return <GenericBlock key={blockIndex} sectionBlock={block} />;
      }
    }
  };

  const nationalAverageBlocks = section.dynamicBlocks.filter(block =>
    ['overall_crime_index_score', 'property_crime_index_score'].includes(
      block.outfields[0],
    ),
  );

  const crimeIndexBlocks = section.dynamicBlocks.filter(
    block =>
      !['overall_crime_index_score', 'property_crime_index_score'].includes(
        block.outfields[0],
      ),
  );

  return (
    <Box
      sx={{ padding: { xs: '0', sm: '0px 24px 20px 24px' }, marginTop: '1rem' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          marginBottom: '10px',
        }}
      >
        {nationalAverageBlocks.length > 0 && (
          <>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1.4rem',
                fontWeight: 900,
                lineHeight: '20px',
                textAlign: 'center',
                color: '#39585C',
              }}
            >
              National Average Crime Index
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                marginBottom: '10px',
              }}
            >
              {nationalAverageBlocks.map((block, blockIndex) =>
                renderBlock(block, blockIndex),
              )}
            </Box>
          </>
        )}
        {crimeIndexBlocks.length > 0 && (
          <>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1.4rem',
                fontWeight: 900,
                lineHeight: '20px',
                textAlign: 'center',
                color: '#39585C',
              }}
            >
              Crime Index
            </Typography>

            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%',
                paddingTop: '0.75rem',
                paddingBottom: '0.25rem',
                paddingX: '0',
              }}
            >
              <BlockNameHeading title={'Block Group'} />
              <BlockNameHeading title={'Block Group'} />
              <BlockNameHeading title={'Block Group'} />
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                marginBottom: '10px',
              }}
            >
              {crimeIndexBlocks.map((block, blockIndex) => (
                <Box
                  key={blockIndex}
                  sx={{
                    maxWidth: {
                      xs: 'calc(50% - 10px)',
                      sm: 'calc(33.33% - 10px)',
                    },
                    flexBasis: {
                      xs: 'calc(50% - 10px)',
                      sm: 'calc(33.33% - 10px)',
                    },
                  }}
                >
                  {renderBlock(block, blockIndex)}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// Demographics-related blocks mirrored from Custom viewer
function DemographicGroupBlock({ group }) {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState(null);

  const handleQuestionMarkClick = key => {
    setActiveOverlay(key);
  };

  const handleCloseOverlay = () => {
    setActiveOverlay(null);
  };

  const sectionInfoAvailable = data['Demographic Group'];

  const style = {
    colorBox: { height: '1rem', width: '1rem' },
    groupTitle: {
      fontFamily: 'Lato',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '20px',
      textAlign: 'left',
      textDecoration: 'underline',
    },
  };

  if (!group) return null;
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1rem',
        paddingX: '1.75rem',
        borderRadius: '1rem',
        display: 'flex',
        gap: '0.75rem',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', gap: '0.75rem' }}>
        <BlockNameHeading title='Demographic Group' />
      </Box>

      <Box sx={{ display: 'flex', gap: '2rem' }}>
        <Box
          sx={[
            style.colorBox,
            { backgroundColor: `#${colors[group] || '000000'}` },
          ]}
        />
        <a target={'_blank'} href={Groups[group]} rel='noreferrer'>
          <Typography sx={style.groupTitle}>{group}</Typography>
        </a>
      </Box>

      {sectionInfoAvailable && (
        <Box
          onClick={() => {
            handleQuestionMarkClick('Demographic Group');
            setIsOverlayVisible(true);
          }}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            marginTop: '1rem',
          }}
        >
          <QuestionMarkSVG />
        </Box>
      )}

      {activeOverlay === 'Demographic Group' && (
        <div
          style={{
            position: 'absolute',
            top: '0%',
            left: '0%',
            width: '16rem',
            zIndex: '1000',
          }}
        >
          <Overlay
            isVisible={isOverlayVisible}
            onClose={() => {
              setIsOverlayVisible(false);
              handleCloseOverlay();
            }}
            keyElement={'Demographic Group'}
          />
        </div>
      )}
    </Box>
  );
}

function NearestGroupsBlock({ groups }) {
  if (!groups) return null;
  const style = {
    colorBox: { height: '1rem', width: '1rem' },
    groupBox: { display: 'flex', gap: '1.25rem', alignItems: 'center' },
    groupTitle: {
      fontFamily: 'Lato',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '20px',
      textAlign: 'left',
      textDecoration: 'underline',
    },
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1.25rem',
        paddingX: '3.5rem',
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          paddingX: '1rem',
          paddingY: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}
      >
        <BlockNameHeading title='Nearest Groups' />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            maxHeight: '10rem',
            overflowY: 'auto',
          }}
        >
          {groups.map(group => (
            <Box key={group} sx={style.groupBox}>
              <Box
                sx={[
                  style.colorBox,
                  { backgroundColor: `#${colors[group] || '000000'}` },
                ]}
              />
              <a target={'_blank'} href={Groups[group]} rel='noreferrer'>
                <Typography sx={style.groupTitle}>{group}</Typography>
              </a>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function WhoWeAreBlock({ blockValue }) {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState(null);

  const handleQuestionMarkClick = key => setActiveOverlay(key);
  const handleCloseOverlay = () => setActiveOverlay(null);
  const sectionInfoAvailable = data['Who we are'];

  if (!blockValue) return null;
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1rem',
        paddingX: '1.75rem',
        borderRadius: '1rem',
        display: 'flex',
        gap: '0.75rem',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', gap: '.5rem' }}>
        <BlockNameHeading title={'Who We Are'} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: '400',
            textAlign: 'justify',
          }}
        >
          {blockValue}
        </Typography>
      </Box>

      {sectionInfoAvailable && (
        <Box
          onClick={() => {
            handleQuestionMarkClick('Who we are');
            setIsOverlayVisible(true);
          }}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            marginTop: '1rem',
          }}
        >
          <QuestionMarkSVG />
        </Box>
      )}

      {activeOverlay === 'Who we are' && (
        <div
          style={{
            position: 'absolute',
            top: '0%',
            left: '0%',
            width: '16rem',
            zIndex: '1000',
          }}
        >
          <Overlay
            isVisible={isOverlayVisible}
            onClose={() => {
              setIsOverlayVisible(false);
              handleCloseOverlay();
            }}
            keyElement={'Who we are'}
          />
        </div>
      )}
    </Box>
  );
}

// LiveMap component for displaying maps
function LiveMap({
  webmapID,
  token,
  addressInfo,
  selectedBlock = null,
  defaultSettings = true,
  staticMapImage = null,
}) {
  const { getAccessToken } = useAuth();
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isClassBreaksLoading, setIsClassBreaksLoading] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [uniqueValueInfos, setUniqueValueInfos] = useState([]);
  const [isCategorical, setIsCategorical] = useState(false);
  const pageSize = 1000;
  const viewRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const MAX_RETRY_ATTEMPTS = 3;

  useEffect(() => {
    // Check if we're on mobile at the beginning of the effect
    if (window.innerWidth <= 768) {
      setMobile(true);
      // Only return early if we have a static map to display
      if (staticMapImage) {
        setIsMapLoaded(true);
        return;
      }
      // Otherwise continue loading the map normally
    }

    let view;
    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/layers/FeatureLayer',
      'esri/widgets/Legend',
      'esri/config',
      'esri/smartMapping/statistics/classBreaks',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/layers/GraphicsLayer',
    ]).then(
      ([
        Map,
        MapView,
        FeatureLayer,
        Legend,
        esriConfig,
        classBreaks,
        WebMap,
        Extent,
        Graphic,
      ]) => {
        if (!webmapID || !token) return;
        esriConfig.apiKey = token;

        let webmap = null;
        const extent = new Extent({
          xmin: addressInfo?.address_xmin,
          ymin: addressInfo?.address_ymin,
          xmax: addressInfo?.address_xmax,
          ymax: addressInfo?.address_ymax,
          spatialReference: {
            wkid: addressInfo?.spatialReference?.wkid,
          },
        });

        if (defaultSettings) {
          webmap = new WebMap({
            portalItem: {
              id: webmapID,
            },
          });

          // Handle layer loading errors on the webmap
          if (typeof webmap.when === 'function') {
            webmap
              .when(() => {
                // Hidden layers on retired Esri services only throw load errors.
                removeRetiredLayers(webmap);
                // Redraw the shaded DISCERN layer from 2026 GeoEnrichment
                // values (flagged; any failure keeps the authored layer).
                applyDiscernShading({
                  webmap,
                  addressInfo,
                  getAccessToken,
                  deps: { FeatureLayer, Graphic },
                  isStale: () => view?.destroyed === true || webmap?.destroyed === true,
                  onUnavailable: () => addUnavailableNotice(view),
                });
                // Check if layers collection exists and has items
                if (webmap.layers && webmap.layers.items) {
                  webmap.layers.items.forEach(layer => {
                    if (layer && typeof layer.when === 'function') {
                      layer.when(
                        () => {
                          console.log(
                            `[MAP_RETRY] Layer ${layer.title} loaded successfully`,
                          );
                        },
                        error => {
                          console.warn(
                            `[MAP_RETRY] Failed to load layer ${layer.title}:`,
                            error,
                          );
                          // Optionally remove the failed layer to prevent it from blocking the map
                          webmap.remove(layer);
                        },
                      );
                    }
                  });
                }
              })
              .catch(error => {
                console.warn(
                  '[MAP_RETRY] Error in webmap initialization:',
                  error,
                );
              });
          }

          view = new MapView({
            container: mapRef.current,
            map: webmap,
            extent: extent,
            zoom: 14,
            navigation: {
              mouseWheelZoomEnabled: false,
            },
          });

          viewRef.current = view;

          view
            .when(() => {
              console.log('[MAP_RETRY] Map loaded successfully');
              setIsMapLoaded(true);
              setRetryCount(0); // Reset retry counter on success
              setIsRetrying(false);
              setErrorMessage(null);
            })
            .catch(err => {
              console.error('[MAP_RETRY] Map view failed to load:', err);
              setIsMapLoaded(false);

              // Handle retry logic
              if (retryCount < MAX_RETRY_ATTEMPTS) {
                setIsRetrying(true);
                const retryAttempt = retryCount + 1;
                console.log(
                  `[MAP_RETRY] Attempt ${retryAttempt}/${MAX_RETRY_ATTEMPTS} will start in 1.5s`,
                );
                setErrorMessage(
                  `Loading failed. Retrying (${retryAttempt}/${MAX_RETRY_ATTEMPTS})...`,
                );

                // Retry after a delay
                const retryTimeout = setTimeout(() => {
                  console.log(
                    `[MAP_RETRY] Starting retry attempt ${retryAttempt}`,
                  );
                  setRetryCount(prevCount => prevCount + 1);

                  // Recreate the view
                  if (viewRef.current) {
                    viewRef.current.destroy();
                  }

                  const retryView = new MapView({
                    container: mapRef.current,
                    map: webmap,
                    extent: extent,
                    zoom: 14,
                    navigation: {
                      mouseWheelZoomEnabled: false,
                    },
                  });

                  viewRef.current = retryView;

                  // Set up event handlers for the retry view
                  retryView
                    .when(() => {
                      console.log(`[MAP_RETRY] Retry attempt successful`);
                      setIsMapLoaded(true);
                      setIsRetrying(false);
                      setRetryCount(0);
                      setErrorMessage(null);

                      // Add UI components to the retry view
                      retryView.ui.add(
                        createResponsiveLegend(Legend, retryView),
                        'top-right',
                      );

                      // Re-add marker if needed
                      if (
                        addressInfo?.address_longitude &&
                        addressInfo?.address_latitude
                      ) {
                        const customSymbol = {
                          type: 'picture-marker',
                          url: marker,
                          width: '50px',
                          height: '100px',
                        };

                        retryView.graphics.add(
                          new Graphic({
                            geometry: {
                              type: 'point',
                              longitude: addressInfo.address_longitude,
                              latitude: addressInfo.address_latitude,
                            },
                            symbol: customSymbol,
                          }),
                        );
                      }
                    })
                    .catch(retryErr => {
                      // If retry also fails, update state
                      console.error(
                        `[MAP_RETRY] Retry ${retryCount + 1} failed:`,
                        retryErr,
                      );
                      setIsRetrying(false);
                      setErrorMessage(retryErr?.message || String(retryErr));
                    });
                }, 1500); // Retry after 1.5 seconds

                // Cleanup timeout when component unmounts
                return () => clearTimeout(retryTimeout);
              } else {
                // Max retries reached, show final error
                console.error(
                  `[MAP_RETRY] Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Giving up.`,
                );
                setIsRetrying(false);
                setErrorMessage(
                  `Failed to load map after ${MAX_RETRY_ATTEMPTS} attempts. ${
                    err?.message || String(err)
                  }`,
                );
              }
            });

          view.ui.add(createResponsiveLegend(Legend, view), 'top-right');
          const customSymbol = {
            type: 'picture-marker',
            url: marker,
            width: '50px',
            height: '100px',
          };

          view.graphics.add(
            new Graphic({
              geometry: {
                type: 'point',
                longitude: addressInfo?.address_longitude,
                latitude: addressInfo?.address_latitude,
              },
              symbol: customSymbol,
            }),
          );
        } else {
          const defaultSym = {
            type: 'simple-fill',
            outline: {
              color: selectedBlock?.boundaries
                ? [57, 88, 92, 1]
                : [255, 255, 255, 0],
              width: '1px',
            },
          };

          let customLayer = new FeatureLayer({
            url: selectedBlock.serviceURL,
            title: selectedBlock.title + ` (${selectedBlock.group})`,
            popupTemplate: {
              title: selectedBlock.title + ` (${selectedBlock.group})`,
              content: `{${selectedBlock?.outfields[0]}}`,
              fieldInfos: [
                {
                  fieldName: selectedBlock?.outfields[0],
                  format: {
                    digitSeparator: true,
                    places: 0,
                  },
                },
              ],
            },
          });

          customLayer.opacity = 0.6;
          customLayer.minScale = 0;
          customLayer.maxScale = 0;

          // Set loading state for class breaks
          setIsClassBreaksLoading(true);
          console.log(
            `[CombinedReportViewer] Starting class breaks request for field: ${selectedBlock?.outfields[0]}`,
          );

          requestThrottler
            .execute(async () => {
              // 2026 GeoEnrichment polygons replace the 2024 Esri layer when the
              // backend supports this block (flagged). A failure never falls back to the
              // 2024 layer: it surfaces an error instead.
              const shadingResult = await requestReportShading({
                selectedBlock,
                addressInfo,
                getAccessToken,
              });
              if (shadingResult.status === 'failed') {
                const unavailable = new Error(SHADING_UNAVAILABLE_MESSAGE);
                unavailable.shadingUnavailable = true;
                throw unavailable;
              }
              const shaded = shadingResult.status === 'shaded' ? shadingResult.data : null;
              if (shaded) {
                customLayer = buildShadedFeatureLayer(
                  { FeatureLayer, Graphic },
                  shaded,
                  selectedBlock,
                  {
                    title: retitleForVintage(
                      selectedBlock.title + ` (${selectedBlock.group})`,
                      shaded.vintage,
                    ),
                  },
                );
              }
              return classBreaks({
                layer: customLayer,
                field: selectedBlock?.outfields[0],
                classificationMethod: 'quantile',
              });
            })
            .then(function (response) {
              console.log(
                `[CombinedReportViewer] Class breaks request successful for field: ${selectedBlock?.outfields[0]}`,
              );
              setIsClassBreaksLoading(false);
              let breakInfos = response.classBreakInfos;

              const dynamicStops = breakInfos.map((info, index) => {
                return {
                  value: info.maxValue,
                  color:
                    index === 0
                      ? '#e66565'
                      : index === 1
                      ? '#f1a178'
                      : index === 2
                      ? '#f9d58d'
                      : index === 3
                      ? '#d9f0ae'
                      : '#a8e9c7',
                  label: info.label,
                };
              });

              customLayer.renderer = {
                type: 'simple',
                symbol: defaultSym,
                label: `U.S. ${selectedBlock.group}`,
                visualVariables: [
                  {
                    type: 'color',
                    field: selectedBlock?.outfields[0],
                    stops: dynamicStops,
                  },
                ],
              };

              completeMapSetup();
            })
            .catch(async error => {
              console.error(
                `[CombinedReportViewer] Class breaks failed for field ${selectedBlock?.outfields[0]}:`,
                error,
              );
              setIsClassBreaksLoading(false);
              // 2026 data unavailable: show the error, never the 2024 layer.
              if (error?.shadingUnavailable) {
                setErrorMessage(SHADING_UNAVAILABLE_MESSAGE);
                setIsMapLoaded(false);
                return;
              }
              setIsCategorical(true);

              try {
                const loadCategoricalData = async (offset = 0) => {
                  const featureResponse = await customLayer.queryFeatures({
                    outFields: [selectedBlock.outfields[0]],
                    returnDistinctValues: true,
                    orderByFields: [selectedBlock.outfields[0]],
                    where: '1=1',
                    returnGeometry: false,
                    f: 'json',
                    num: 3500,
                    start: offset,
                  });

                  const uniqueValues = featureResponse.features
                    .map(
                      feature => feature.attributes[selectedBlock.outfields[0]],
                    )
                    .filter(value => value !== null && value !== undefined);

                  const valueInfos = uniqueValues.map((value, index) => ({
                    value: value,
                    symbol: {
                      type: 'simple-fill',
                      color: [
                        '#a8e9c7',
                        '#d9f0ae',
                        '#f9d58d',
                        '#f1a178',
                        '#e66565',
                      ][index % 5],
                      outline: {
                        color: selectedBlock?.boundaries
                          ? [57, 88, 92, 1]
                          : [255, 255, 255, 0],
                        width: '1px',
                      },
                    },
                    label: value.toString(),
                  }));

                  setUniqueValueInfos(valueInfos);

                  customLayer.renderer = {
                    type: 'unique-value',
                    field: selectedBlock.outfields[0],
                    uniqueValueInfos: valueInfos,
                    label: `U.S. ${selectedBlock.group}`,
                  };

                  return uniqueValues.length === 0;
                };

                await loadCategoricalData(offset);
                completeMapSetup();
              } catch (err) {
                console.error('Error setting up categorical renderer:', err);
                customLayer.renderer = {
                  type: 'simple',
                  symbol: defaultSym,
                  label: `U.S. ${selectedBlock.group}`,
                };
                completeMapSetup();
              }
            });

          function completeMapSetup() {
            const map = new Map({
              basemap: 'gray-vector',
              layers: [customLayer],
            });

            view = new MapView({
              container: mapRef.current,
              map: map,
              extent: extent,
              navigation: {
                mouseWheelZoomEnabled: false,
              },
              // scale:
              //   customLayer.minScale === 0
              //     ? customLayer.maxScale
              //     : customLayer.minScale,
              zoom: 14,
            });

            viewRef.current = view;

            view
              .when(() => {
                console.log(
                  '[MAP_RETRY] Feature layer map loaded successfully',
                );
                setIsMapLoaded(true);
                setRetryCount(0); // Reset retry counter on success
                setIsRetrying(false);
                setErrorMessage(null);
              })
              .catch(err => {
                console.error(
                  '[MAP_RETRY] Feature layer map failed to load:',
                  err,
                );
                setIsMapLoaded(false);

                // Handle retry logic
                if (retryCount < MAX_RETRY_ATTEMPTS) {
                  setIsRetrying(true);
                  const retryAttempt = retryCount + 1;
                  console.log(
                    `[MAP_RETRY] Feature layer attempt ${retryAttempt}/${MAX_RETRY_ATTEMPTS} will start in 1.5s`,
                  );
                  setErrorMessage(
                    `Loading failed. Retrying (${retryAttempt}/${MAX_RETRY_ATTEMPTS})...`,
                  );

                  // Retry after a delay
                  const retryTimeout = setTimeout(() => {
                    console.log(
                      `[MAP_RETRY] Starting feature layer retry attempt ${retryAttempt}`,
                    );
                    setRetryCount(prevCount => prevCount + 1);

                    // Recreate the view
                    if (viewRef.current) {
                      viewRef.current.destroy();
                    }

                    const retryView = new MapView({
                      container: mapRef.current,
                      map: map,
                      extent: extent,
                      navigation: {
                        mouseWheelZoomEnabled: false,
                      },
                      zoom: 14,
                    });

                    viewRef.current = retryView;

                    // Set up event handlers for the retry view
                    retryView
                      .when(() => {
                        console.log(
                          `[MAP_RETRY] Feature layer retry attempt successful`,
                        );
                        setIsMapLoaded(true);
                        setIsRetrying(false);
                        setRetryCount(0);
                        setErrorMessage(null);

                        // Add UI components to the retry view
                        retryView.ui.add(
                          new Legend({
                            view: retryView,
                          }),
                          'top-right',
                        );

                        // Re-add marker if needed
                        if (
                          addressInfo?.address_longitude &&
                          addressInfo?.address_latitude
                        ) {
                          const customSymbol = {
                            type: 'picture-marker',
                            url: marker,
                            width: '50px',
                            height: '100px',
                          };

                          retryView.graphics.add(
                            new Graphic({
                              geometry: {
                                type: 'point',
                                longitude: addressInfo.address_longitude,
                                latitude: addressInfo.address_latitude,
                              },
                              symbol: customSymbol,
                            }),
                          );
                        }
                      })
                      .catch(retryErr => {
                        // If retry also fails, update state
                        console.error(
                          `[MAP_RETRY] Retry ${retryCount + 1} failed:`,
                          retryErr,
                        );
                        setIsRetrying(false);
                        setErrorMessage(retryErr?.message || String(retryErr));
                      });
                  }, 1500); // Retry after 1.5 seconds

                  // Cleanup timeout when component unmounts
                  return () => clearTimeout(retryTimeout);
                } else {
                  // Max retries reached, show final error
                  console.error(
                    `[MAP_RETRY] Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached for feature layer map. Giving up.`,
                  );
                  setIsRetrying(false);
                  setErrorMessage(
                    `Failed to load map after ${MAX_RETRY_ATTEMPTS} attempts. ${
                      err?.message || String(err)
                    }`,
                  );
                }
              });

            view.ui.add(
              new Legend({
                view: view,
              }),
              'top-right',
            );

            const customSymbol = {
              type: 'picture-marker',
              url: marker,
              width: '50px',
              height: '100px',
            };

            const center = {
              longitude:
                addressInfo?.address_longitude ||
                (extent.xmin + extent.xmax) / 2,
              latitude:
                addressInfo?.address_latitude ||
                (extent.ymin + extent.ymax) / 2,
            };

            view.graphics.add(
              new Graphic({
                geometry: {
                  type: 'point',
                  longitude: center.longitude,
                  latitude: center.latitude,
                },
                symbol: customSymbol,
              }),
            );
          }
        }
      },
    );

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, [webmapID, token, selectedBlock, defaultSettings, currentPage]);

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
    setOffset(prev => prev + 3500);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
    setOffset(prev => Math.max(0, prev - 3500));
  };

  // If we're on mobile and have a static map image, show it
  if (mobile && staticMapImage) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          position: 'relative',
          justifyContent: 'center',
          alignItems: 'center',
          height: {
            sm: '30rem',
            xs: '20rem',
          },
        }}
      >
        <img
          src={staticMapImage}
          alt='Map'
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          sm: '30rem',
          xs: '20rem',
        },
        position: 'relative',
      }}
    >
      {(!isMapLoaded || isClassBreaksLoading) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Box
              sx={{
                p: 2,
                maxWidth: '90%',
                bgcolor: 'rgba(255,255,255,0.95)',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{ color: '#b00020', mb: 0.5 }}
              >
                Error loading map
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: '#333', mb: isRetrying ? 0 : 2 }}
              >
                {errorMessage}
              </Typography>

              {isRetrying ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: '#4FA490',
                    mt: 1,
                  }}
                />
              ) : retryCount >= MAX_RETRY_ATTEMPTS ? (
                <Button
                  variant='contained'
                  sx={{
                    mt: 1,
                    backgroundColor: '#4FA490',
                    '&:hover': {
                      backgroundColor: '#39585C',
                    },
                  }}
                  onClick={() => {
                    console.log('[MAP_RETRY] Manual retry initiated by user');
                    setRetryCount(0);
                    setErrorMessage(null);
                    // Force effect to re-run
                    viewRef.current = null;
                    setTimeout(() => {
                      if (viewRef.current) {
                        viewRef.current.destroy();
                        viewRef.current = null;
                      }
                    }, 100);
                  }}
                >
                  Try Again
                </Button>
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress
                sx={{
                  color: '#4FA490',
                }}
              />
              <Typography
                variant='caption'
                sx={{
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                {isClassBreaksLoading
                  ? 'Processing map data...'
                  : 'Loading map...'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      ></Box>

      {isCategorical && isMapLoaded && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginTop: '0.5rem',
            position: 'absolute',
            bottom: '10px',
            left: 0,
            right: 0,
          }}
        >
          <Button
            variant='contained'
            disabled={currentPage === 0}
            onClick={handlePrevPage}
            sx={{
              backgroundColor: '#4FA490',
              '&:hover': {
                backgroundColor: '#39585C',
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
              },
            }}
          >
            Previous Page
          </Button>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
            }}
          >
            Page {currentPage + 1} ({uniqueValueInfos.length} items)
          </Typography>
          <Button
            variant='contained'
            onClick={handleNextPage}
            disabled={uniqueValueInfos.length < pageSize}
            sx={{
              backgroundColor: '#4FA490',
              '&:hover': {
                backgroundColor: '#39585C',
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
              },
            }}
          >
            Next Page
          </Button>
        </Box>
      )}
    </Box>
  );
}

// Helper function for responsive legend
const createResponsiveLegend = (Legend, view) => {
  const style = document.createElement('style');
  style.textContent = `
    .esri-legend {
      max-height: 150px !important;
      max-width: 150px !important;
      overflow-y: auto !important;
      overflow-x: auto !important;
      background-color: rgba(255, 255, 255, 0.9) !important;
    }

    @media screen and (min-width: 600px) {
      .esri-legend {
        max-height: 300px !important;
        max-width: 250px !important;
      }
    }

    .esri-legend__service {
      padding: 8px !important;
    }
  `;
  document.head.appendChild(style);

  return new Legend({
    view: view,
  });
};

function Sidebar({ group, activeSection, publicView }) {
  const navigate = useNavigate();

  const styles = {
    selected: {
      backgroundColor: '#39585C',
      color: '#FFFFFF',
    },
    unselected: {
      color: '#39585C',
      fontWeight: 400,
      fontStyle: 'normal',
    },
    itemBoxStyle: {
      display: 'flex',
      height: '1.1rem',
      padding: '1rem',
      alignItems: 'center',
      gap: '0.75rem',
      alignSelf: 'stretch',
      borderRadius: '0.75rem',
    },
    itemTextStyle: {
      fontFamily: '"Lato", sans-serif',
      fontSize: {
        xs: '0.875rem',
        sm: '.75rem',
        lg: '0.875rem',
      },
      fontStyle: 'normal',
      fontWeight: 800,
      lineHeight: '150%',
      letterSpacing: '0.0125rem',
    },
    upperBoxInnerStyle: {
      boxSizing: 'border-box',
      backgroundColor: '#4FA490',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.75rem',
      borderRadius: '1rem',
      padding: '0.75rem 0.2rem 0.75rem 0.2rem',
      cursor: 'pointer',
    },
    upperBoxTypography: {
      fontFamily: '"Lato", sans-serif',
      fontSize: {
        sm: '16px',
        xs: '0.8rem',
      },
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: '0.2px',
      textAlign: 'center',
      color: '#FFFFFF',
    },
    lowerBoxInnerStyle: {
      boxSizing: 'border-box',
      backgroundColor: '#4FA490',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      borderRadius: '1.2rem',
      padding: '16px',
      cursor: 'pointer',
    },
  };

  const handleClick = (reportId, sectionKey) => {
    const targetSection = document.getElementById(`${reportId}-${sectionKey}`);
    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Use reports list for template -> sections grouping
  const reports = group?.reports || [];

  const getSidebarIcon = (key, selected) => {
    const style = { height: '1.25rem', width: '1.25rem' };
    switch (key) {
      case 'demographics':
        return selected ? (
          <DemoIconLight style={style} />
        ) : (
          <DemoIconDark style={style} />
        );
      case 'income':
        return selected ? (
          <IncomeIconLight style={style} />
        ) : (
          <IncomeIconDark style={style} />
        );
      case 'hpi':
        return selected ? (
          <HPIIconLight style={style} />
        ) : (
          <HPIIconDark style={style} />
        );
      case 'schools':
        return selected ? (
          <SchoolsIconLight style={style} />
        ) : (
          <SchoolsIconDark style={style} />
        );
      case 'crime':
        return selected ? (
          <CrimeIconLight style={style} />
        ) : (
          <CrimeIconDark style={style} />
        );
      case 'expansion':
        return selected ? (
          <ExpansionIconLight style={style} />
        ) : (
          <ExpansionIconDark style={style} />
        );
      case 'rent':
        return selected ? (
          <RentIconLight style={style} />
        ) : (
          <RentIconDark style={style} />
        );
      case 'neighborhood':
        return selected ? (
          <NeighborhoodIconLight style={style} />
        ) : (
          <NeighborhoodIconDark style={style} />
        );
      case 'msa':
        return selected ? (
          <MSAIconLight style={style} />
        ) : (
          <MSAIconDark style={style} />
        );
      case 'ai_analysis':
        return selected ? (
          <AiAnalysisLight style={style} />
        ) : (
          <AiAnalysisDark style={style} />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '0',
        left: '0',
        height: '100vh',
        overflowY: 'auto',
        width: {
          sm: '19%',
          xs: '100%',
        },
        backgroundColor: '#F9F9F9',
        display: 'flex',
        gap: '2rem',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          marginTop: '13.5rem',
          marginBottom: '5rem',
          width: '85%',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.8rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.8rem',
            width: '96%',
          }}
        >
          {AGENT_ENABLED && !publicView && (
            <Box
              sx={styles.upperBoxInnerStyle}
              onClick={() => {
                if (group?.reports?.[0]?.chatID) {
                  navigate(`/agent?chatID=${group.reports[0].chatID}`);
                }
              }}
            >
              <MessageSquare style={{ color: 'white' }} />
              <Typography sx={styles.upperBoxTypography}>Agent</Typography>
            </Box>
          )}

          <ShareModal groupData={group} />
        </Box>

        {/* PDF and Pro Report actions are intentionally removed for Combined viewer */}

        {/* Template -> Sections Navigation */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '96%',
            position: 'sticky',
            top: '0',
          }}
        >
          {reports.map((report, index) => (
            <Accordion
              key={report._id || index}
              defaultExpanded={false}
              sx={{
                backgroundColor: '#F9F9F9',
                boxShadow: 'none',
                borderRadius: '12px',
              }}
            >
              <AccordionSummary
                sx={{
                  ...styles.lowerBoxInnerStyle,
                  backgroundColor: '#F9F9F9',
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Lato", sans-serif',
                    fontSize: {
                      sm: '14px',
                      xs: '0.9rem',
                    },
                    fontWeight: 800,
                    color: '#39585C',
                  }}
                >
                  {report?.templateName ||
                    report?.template?.templateName ||
                    'Default Template'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '8px 12px' }}>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  {(function () {
                    const sections = Array.isArray(report?.sections)
                      ? [...report.sections]
                      : [];
                    const templateName =
                      report?.templateName || report?.template?.templateName;
                    if (templateName === 'Default (DISCERN)') {
                      const order = [
                        'demographics',
                        'income',
                        'hpi',
                        'schools',
                        'crime',
                        'expansion',
                        'rent',
                        'neighborhood',
                        'msa',
                      ];
                      sections.sort(
                        (a, b) => order.indexOf(a.key) - order.indexOf(b.key),
                      );
                    }
                    return sections;
                  })()
                    .filter(section => !section.key.includes('msa'))
                    .map(section => (
                      <Box
                        key={`${report._id}-${section.key}`}
                        sx={[
                          styles.lowerBoxInnerStyle,
                          activeSection === `${report._id}-${section.key}`
                            ? { backgroundColor: '#39585C' }
                            : { backgroundColor: '#F9F9F9' },
                        ]}
                        onClick={() => handleClick(report._id, section.key)}
                      >
                        <Box sx={{ width: '1.5rem', height: '1.5rem' }}>
                          {getSidebarIcon(
                            section.key,
                            activeSection === `${report._id}-${section.key}`,
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: '"Lato", sans-serif',
                            fontSize: { sm: '14px', xs: '0.8rem' },
                            textAlign: 'left',
                            fontWeight:
                              activeSection === `${report._id}-${section.key}`
                                ? 800
                                : 400,
                            color:
                              activeSection === `${report._id}-${section.key}`
                                ? '#FFFFFF'
                                : '#39585C',
                          }}
                        >
                          {section.title.includes('Income')
                            ? 'Income'
                            : section.title.includes('MSA')
                            ? 'Metropolitan Statistical Area'
                            : titleOf(section)}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ShareModal({ groupData }) {
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const reportIDs = groupData?.reports?.map(report => report._id).join(',');
  const shareableLink = `${window.location.origin}/shared-combined-report?reportIDs=${reportIDs}`;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareableLink);
    setSnackbarOpen(true);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          boxSizing: 'border-box',
          backgroundColor: '#4FA490',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.75rem',
          borderRadius: '1rem',
          padding: '0.75rem 0.2rem 0.75rem 0.2rem',
          cursor: 'pointer',
        }}
      >
        <Share
          style={{ width: '1.5rem', height: '1.5rem', color: '#FFFFFF' }}
        />
        <Typography
          style={{
            fontFamily: '"Lato", sans-serif',
            fontSize: {
              sm: '16px',
              xs: '0.8rem',
            },
            fontWeight: 700,
            lineHeight: '24px',
            letterSpacing: '0.2px',
            textAlign: 'center',
            color: '#FFFFFF',
          }}
        >
          Share Combined Report
        </Typography>
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle
          sx={{
            width: {
              sm: '30rem',
              xs: '100%',
            },
          }}
        >
          Share Combined Report
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={shareableLink}
            InputProps={{
              readOnly: true,
            }}
            sx={{
              textWrap: 'wrap',
            }}
            margin='normal'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            sx={{
              color: '#4FA490',
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleCopy}
            variant='contained'
            sx={{
              backgroundColor: '#4FA490',
              color: '#FFFFFF',
            }}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message='Link copied to clipboard'
      />
    </>
  );
}

export default CombinedReportViewer;
